import {
  useEffectWithController,
  usePromise
} from '@carvana/merch-react-hooks';
import { useContext, useRef, useState } from 'react';
import RouteContext from '~/context/srp/RouteContext';
import type { SeoContent, SeoLinks, SeoReviews } from '~/contracts/common/Seo';
import type { SeoProps } from '~/server-props/srp/contracts';
import {
  fetchSeoContent,
  fetchSeoLinks,
  fetchSeoReviews
} from '~/services/srp/seoService';

function useEffectFetch<T>(
  initialState: T | undefined,
  client: (
    pathname: string,
    controller: AbortController
  ) => Promise<T | undefined>
): T | undefined {
  const { currentPath } = useContext(RouteContext);
  const mounted = usePromise();
  const prevPathname = useRef(currentPath);
  const [data, setData] = useState(initialState);

  useEffectWithController(
    controller => {
      if (initialState && currentPath === prevPathname.current) return;

      prevPathname.current = currentPath;

      if (
        currentPath.includes('/cars/filters') ||
        currentPath.includes('/cars-v1/filters')
      ) {
        setData(undefined);
        
        return;
      }

      mounted(client(currentPath, controller)).then(setData);
    },
    [client, currentPath, initialState, mounted]
  );

  return data;
}

export default function useSeoData(initialSeoProps?: SeoProps) {
  const seoContent = useEffectFetch<SeoContent>(
    initialSeoProps?.content,
    fetchSeoContent
  );
  const seoLinks = useEffectFetch<SeoLinks>(
    initialSeoProps?.links,
    fetchSeoLinks
  );
  const seoReviews = useEffectFetch<SeoReviews>(
    initialSeoProps?.reviews,
    fetchSeoReviews
  );

  return {
    seoContent,
    seoLinks,
    seoReviews
  };
}
